import { QuestionText, questionTextToPlainText } from 'utils/questionMarkdown';
import React from 'react';
import { Button } from 'components/ui/button';
import { Question } from 'types';
import { cn } from 'lib/utils';

interface Props {
  question: Question;
  booleanValue: boolean | null;
  showAnswerFeedback?: boolean;
  onChange: (value: boolean) => void;
}

export const StyledBooleanQuestion: React.FC<Props> = ({
  question,
  booleanValue,
  showAnswerFeedback = true,
  onChange,
}) => {
  const [trueLabel, falseLabel] = React.useMemo(() => {
    const fallbackTrue = question.high_text || 'True';
    const fallbackFalse = question.low_text || 'False';
    return [fallbackTrue, fallbackFalse];
  }, [question.high_text, question.low_text]);

  const selectedLabel =
    booleanValue === true ? trueLabel : booleanValue === false ? falseLabel : undefined;
  const selectedIndex = booleanValue === true ? 1 : booleanValue === false ? 2 : undefined;
  const hasCorrectOptionIndexes = Array.isArray(question.correct_option_indexes) && question.correct_option_indexes.length > 0;
  const isCorrect =
    selectedIndex != null && hasCorrectOptionIndexes
      ? question.correct_option_indexes?.includes(selectedIndex)
      : booleanValue != null && question.correct_value
      ? selectedLabel?.trim() === question.correct_value.trim()
      : undefined;

  return (
    <>
      <div className="grid w-full grid-cols-2 gap-4 self-stretch">
        <BooleanButton
          selected={booleanValue === true}
          label={<QuestionText text={trueLabel} citations={question.citations} inline links={false} />}
          onClick={() => onChange(true)}
        />

        <BooleanButton
          selected={booleanValue === false}
          label={<QuestionText text={falseLabel} citations={question.citations} inline links={false} />}
          onClick={() => onChange(false)}
        />
      </div>

      {showAnswerFeedback && isCorrect != null && booleanValue != null && (
        <BooleanAnswerFeedback
          isCorrect={isCorrect}
          correctLabel={isCorrect ? selectedLabel ?? '' : booleanValue ? falseLabel : trueLabel}
          explanation={question.explanation}
        />
      )}
    </>
  );
};

/** Lowercases one-word answers like TRUE or Yes so they read naturally inside a sentence. */
const answerInSentence = (label: string) => {
  const plain = questionTextToPlainText(label).trim();
  return /^(true|false|yes|no)$/i.test(plain) ? plain.toLowerCase() : plain;
};

/** Splits a trailing citation such as "Author et al. (2008), Journal" from the explanation body. */
export const splitExplanationSource = (explanation: string) => {
  const yearIndex = explanation.search(/\(\d{4}\)/);
  if (yearIndex < 0) return { body: explanation, source: '' };
  for (let index = yearIndex; index > 0; index -= 1) {
    if (
      explanation[index] === '.' &&
      /\s/.test(explanation[index + 1] ?? '') &&
      !/\bet al$/.test(explanation.slice(0, index))
    ) {
      return { body: explanation.slice(0, index + 1).trim(), source: explanation.slice(index + 1).trim() };
    }
  }
  return { body: explanation, source: '' };
};

const BooleanAnswerFeedback: React.FC<{
  isCorrect: boolean;
  correctLabel: string;
  explanation?: string | null;
}> = ({ isCorrect, correctLabel, explanation }) => {
  const answer = answerInSentence(correctLabel);
  const { body, source } = splitExplanationSource(typeof explanation === 'string' ? explanation.trim() : '');

  return (
    <div
      className="mt-5 w-full rounded-2xl bg-white/85 px-5 py-4 text-foundation-base-darkbase-dark-500 shadow-elevation-sm"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="boolean-answer-feedback"
    >
      <p className="type-body-m-semibold">
        {isCorrect ? "That's right." : 'Not quite.'}
        {answer ? ` The answer is ${answer}.` : ''}
      </p>
      {body ? <p className="mt-1.5 type-body-m">{body}</p> : null}
      {source ? <p className="mt-2 text-xs leading-4 text-foundation-base-darkbase-dark-300">{source}</p> : null}
    </div>
  );
};

interface BooleanButtonProps {
  selected: boolean;
  label: React.ReactNode;
  onClick: () => void;
}

export const BooleanButton: React.FC<BooleanButtonProps> = ({
  selected,
  label,
  onClick,
}) => {
  return (
    <Button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex h-28 flex-col items-center justify-center gap-1 rounded-2xl border-2 bg-white px-2 py-4 shadow-elevation-sm transition-colors hover:bg-white sm:h-32 focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2",
        selected
          ? "border-foundation-base-darkbase-dark-500"
          : "border-transparent hover:border-foundation-base-darkbase-dark-100"
      )}
    >
      <span className="whitespace-normal text-center font-headings-h3 text-[1.625rem] leading-8 text-foundation-base-darkbase-dark-500">
        {label}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          "whitespace-nowrap text-xs font-semibold uppercase tracking-[0.06em]",
          selected ? "text-foundation-base-darkbase-dark-500" : "text-transparent"
        )}
      >
        Your answer
      </span>
    </Button>
  );
};
